//
// Copyright (c) 2025 Comcast
//

import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import {
  useFetchTicketChildrenQuery,
} from 'api/tickets'

import {
  useLocationQueryParams,
} from 'utils/hooks'
import { useExpandablePageSection } from 'components/Shared/ExpandablePageSection/hooks'
import { useCurrentTicketingSystem } from 'components/App/TicketsPage/shared/hooks'

import LoadingIndicator from 'components/Shared/LoadingIndicator/Index'
import ExpandablePageSection from 'components/Shared/ExpandablePageSection/Index'
import Pagination from 'components/Shared/Pagination/Index'
import TicketsTable from 'components/App/TicketsPage/shared/TicketsTable/Index'

const TicketChildren = ({ ticket }) => {
  const { t } = useTranslation()
  const {
    isExpanded,
    toggleExpanded,
  } = useExpandablePageSection()
  const currentTicketingSystem = useCurrentTicketingSystem()
  const providerTicketBoard = currentTicketingSystem?.providerTicketBoards[0] || {}

  const [locationQueryParams] = useLocationQueryParams()

  const activePage = parseInt(locationQueryParams['children_tickets_page']) || 1

  const noTicket = Boolean(!ticket.id)
  const withHierarchy = ticket.hierarchyEnabled

  // use currentData to clear children on ticket change
  const { currentData: data, isFetching } = useFetchTicketChildrenQuery({
    ticketingSystemDisplayId: ticket.ticketingSystemDisplayId,
    parentTicketId: ticket.id,
    page: activePage,
  },
  {
    skip: noTicket || !ticket.childTicketsCount || !withHierarchy,
  })

  const tickets = data?.tickets || []
  const ticketsCount = (isFetching ? ticket.childTicketsCount : data?.ticketsCount) || 0
  const totalPages = data?.totalPages || 1
  const partnerStatusesMap = data?.partnerStatusesMap || {}

  if (noTicket || !ticketsCount || !withHierarchy) {
    return null
  }

  return (
    <ExpandablePageSection
      id='children'
      title={t('ticketPage.childrenSection.title')}
      badgeText={ticketsCount}
      isExpanded={isExpanded}
      onToggleExpanded={toggleExpanded}
      dataTest='ticket-children-section'
    >
      <LoadingIndicator loading={isFetching} small />
      <TicketsTable
        tickets={tickets}
        ticketingSystem={currentTicketingSystem}
        partnerStatusesMap={partnerStatusesMap}
        ticketBonding={currentTicketingSystem?.ticketBonding}
        providerTicketBoard={providerTicketBoard}
        withExpandableRows
      />
      <Pagination
        activePage={activePage}
        totalPages={totalPages}
        disabled={isFetching}
        pageParamName='children_tickets_page'
      />
    </ExpandablePageSection>
  )
}

TicketChildren.propTypes = {
  ticket: PropTypes.object.isRequired,
}

export default TicketChildren
